/*
 * Copyright (c) 2020. American Express Company
 */

// theirs
import _ from 'lodash';
import { ChronoUnit } from '@js-joda/core';
import React, { Component, ReactComponentElement, RefObject } from 'react';

// models and services
import { AccountModel } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';
import { SavingsDetailsViewModel } from 'src/types/view-models/savings-details.view.model';
import { FullUserModel } from 'src/types/user.model';

// components
import { FieldControl, IconHide, IconShow, IconTrash, Label, Select, SelectOption } from '@americanexpress/dls-react';
import GoalProgressBar from '../goal-progress-bar/GoalProgressBar';
import IconLink from '../icon-link/IconLink';
import InfoCallout from '../callout/InfoCallout';
import SavingsPerMonth from '../savings-per-month/SavingsPerMonth';
import StatusCallout from '../callout/StatusCallout';

// icons and styles
import styles from './SavingsDetails.module.scss';
import HighYieldSavingsCallout from 'src/components/callout/HighYieldSavingsCallout';
import Selectors from 'src/utils/selectors';

interface SavingsDetailsState {
    accountBalance?: number;
    sharingGoals?: number;
    percentSaved?: number;
    amountSaved?: number;
    targetAmount?: number;
    leftToSave?: number;
    perMonth?: number;
    surplus?: number;
    perMonthNeeded?: number;
    savingsAccounts?: AccountModel[];
}

export default class SavingsDetails extends Component<SavingsDetailsViewModel> {
    private readonly accountSelect: RefObject<any> = React.createRef();
    private saveTimer: number = undefined;

    public override state: SavingsDetailsState = {};

    public static getDerivedStateFromProps(props: any, _: any): any {
        return SavingsDetails.calcSavings(props);
    }

    private static calcSavings(props: Readonly<any>): any {
        const card: CardModel = props.card;
        const user: FullUserModel = props.user;
        const accounts: AccountModel[] = _.get(props, 'user.accounts');
        const savingsAccounts = _.filter(accounts, (account: AccountModel) => account.subtype === 'savings');
        const months: number = card.startDate.until(card.endDate).toTotalMonths();
        const surplus: number = SavingsDetails.getMonthlyAvailableForCard(card, props.originalCard);
        const accountId: number = card.accountId;
        const target: number = _.get(card, 'attributes.targetBalance') || 0;
        const amountSaved: number = Math.min(card.amountSaved, target) || 0;
        const percentSaved: number = Math.round((100 * amountSaved) / target) || 0;
        let leftToSave: number = card.attributes.targetBalance || 0;
        let perMonthNeeded = Math.ceil(Math.max(0, leftToSave / months)) || 0;
        let account: AccountModel;
        let accountBalance: number;
        let sharingGoals: number;

        if (accountId && savingsAccounts) {
            account = _.find(savingsAccounts, { id: accountId });
            const goals: CardModel[] = _.filter(props.user.cards, { accountId });
            sharingGoals = goals.length;
            const currentCard: CardModel = _.find(user.cards, { id: card.id });
            if (!currentCard) {
                return;
            }
            if (currentCard.accountId !== card.accountId) {
                sharingGoals++;
            }

            if (account) {
                accountBalance = account.current || 0;
                leftToSave = Math.max(0, leftToSave - accountBalance / sharingGoals) || 0;
                // recalc amount needed per month
                perMonthNeeded = Math.ceil(Math.max(0, leftToSave / months)) || 0;
            }
        }

        return {
            accountBalance,
            sharingGoals,
            percentSaved,
            amountSaved,
            targetAmount: target,
            leftToSave,
            perMonth: _.isNumber(card.attributes.perMonth) ? card.attributes.perMonth : perMonthNeeded,
            surplus,
            perMonthNeeded,
            savingsAccounts
        };
    }

    public override render(): ReactComponentElement<any> {
        const card: CardModel = this.props.card;

        const progress: JSX.Element = (
            <GoalProgressBar
                currentAmount={this.state.amountSaved}
                percent={this.state.percentSaved}
                targetAmount={this.state.targetAmount}
            />
        );

        const accountSelector: JSX.Element = (
            <FieldControl>
                <Label htmlFor="accountId" id="accountIdLabel">
                    Select an account to save to
                </Label>
                <Select
                    id="accountId"
                    value={card.accountId ? card.accountId : ''}
                    onChange={(e: any) => {
                        this.handleAccount(e);
                    }}
                    required
                    fluid
                    ref={this.accountSelect}
                    data-cy={Selectors.PlanAhead.ACCOUNT_SELECT}
                >
                    <SelectOption />
                    {SavingsDetails.getAccountOptions(this.state.savingsAccounts)}
                </Select>
            </FieldControl>
        );

        let status: JSX.Element = null;

        if (!_.isNumber(this.state.leftToSave)) {
            status = <StatusCallout status="error" icon="warning" text="Enter a savings goal." />;
        } else if (this.state.perMonth > 0) {
            if (this.state.leftToSave > 0) {
                if (this.state.perMonth > this.state.surplus) {
                    status = (
                        <StatusCallout
                            status="error"
                            icon="warning"
                            text={LiteralsService.getLiteral('SavingsDrawer.notComfortableSave')}
                        />
                    );
                } else {
                    if (this.state.perMonth >= this.state.perMonthNeeded) {
                        status = (
                            <StatusCallout
                                status="success"
                                icon="check"
                                text={LiteralsService.getLiteral('SavingsDrawer.onTime')}
                            />
                        );
                    } else {
                        status = (
                            <StatusCallout
                                status="error"
                                icon="warning"
                                text={LiteralsService.getLiteral('SavingsDrawer.notOnTime')}
                            />
                        );
                    }
                }
            } else {
                status = (
                    <StatusCallout
                        status="success"
                        icon="check"
                        text={LiteralsService.getLiteral('SavingsDrawer.met')}
                    />
                );
            }
        }

        const howMuchPerMonth: JSX.Element = (
            <SavingsPerMonth
                delta={this.state.perMonthNeeded}
                surplus={Math.max(this.state.surplus, 0)}
                inputLabel="Savings amount"
                inputValue={Math.max(0, this.state.perMonth)}
                inputOnChange={(value: number) => {
                    this.setState({ perMonth: value || 0 });
                    if (this.saveTimer) {
                        clearTimeout(this.saveTimer);
                    }
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    this.saveTimer = window.setTimeout(async () => {
                        this.props.card.attributes.perMonth = value || 0;
                        await this.props.handleUpdateCard(this.props.card);
                    }, 1000);
                }}
                status={card.accountId && this.state.perMonth ? status : null}
            />
        );

        return (
            <div className={styles.savingsDetails}>
                {progress}
                {this.props.isExpanded && (
                    <>
                        <div className="drawerSection">
                            {accountSelector}
                            {_.isNumber(this.state.accountBalance) && (
                                <InfoCallout noMargin>
                                    Current balance: {FormatService.format(this.state.accountBalance)}
                                    {this.state.sharingGoals > 0 && (
                                        <span>
                                            {' '}
                                            shared between {this.state.sharingGoals}{' '}
                                            {this.state.sharingGoals === 1 ? 'goal' : 'goals'}
                                        </span>
                                    )}
                                </InfoCallout>
                            )}
                        </div>
                        <HighYieldSavingsCallout />
                        {howMuchPerMonth}
                    </>
                )}
                <IconLink
                    icon={this.props.isExpanded ? <IconHide /> : <IconShow />}
                    text={this.props.isExpanded ? 'Hide savings details' : 'Show savings details'}
                    onClick={this.props.toggleExpanded}
                    id="show-hide-saving-details"
                />
                {this.props.isExpanded && (
                    <IconLink
                        icon={<IconTrash />}
                        text="Delete savings goal"
                        onClick={() => {
                            this.props.handleDeleteCard(this.props.card, false);
                        }}
                        id="delete-savings-goal"
                    />
                )}
            </div>
        );
    }

    private handleAccount(e: any): void {
        e.target.blur();
        const accountId = Number(e.target.value);

        // TODO I don't know why changing the card doesn't also change the one in user.cards
        // update the card in the user list (separate copy of the current card)
        const userCard = _.find(this.props.user.cards, { id: this.props.card.id });
        userCard.accountId = accountId;

        // update the card
        const updatedCard: CardModel = this.props.card;
        _.set(updatedCard, 'accountId', accountId);
        this.props.handleUpdateCard(updatedCard);
    }

    /** Get the monthlyAvailable available to save from the extended/edit card payload */
    private static getMonthlyAvailableForCard(savingsCard: CardModel, card: CardModel): number {
        // just in case the original card does not have detailed payload in the card factory
        if ((card as any).monthlyFreeCash === undefined) return 0;

        const endingFreeCash: number = (card as any).monthlyFreeCash[savingsCard.endDate.toJSON()];
        const months = savingsCard.startDate.until(savingsCard.endDate, ChronoUnit.MONTHS);
        if (months === 0) return 0;
        return Math.floor(endingFreeCash / months);
    }

    private static getAccountOptions(accounts: AccountModel[]): JSX.Element[] {
        if (accounts == null) return null;
        return accounts.map((account: AccountModel) => {
            return (
                <SelectOption key={account.id} value={account.id}>
                    {account.name}
                </SelectOption>
            );
        });
    }
}
